import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaUsers, FaShoppingCart, FaStore, FaExclamationTriangle, FaChartLine, FaUserCheck, FaUserSlash, FaBox, FaBoxOpen, FaBook, FaLaptop, FaPencilAlt, FaEye, FaTrash } from 'react-icons/fa';

import { getAdminDashboard } from '../../services/adminService';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentProducts, setRecentProducts] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Fetch dashboard data
  useEffect(() => {
    let mounted = true;

    const fetchDashboard = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getAdminDashboard();
        if (!mounted) return;
        setStats(data?.stats || null);
        setRecentUsers(Array.isArray(data?.recentUsers) ? data.recentUsers : []);
        setRecentProducts(Array.isArray(data?.recentProducts) ? data.recentProducts : []);
        setRecentOrders(Array.isArray(data?.recentOrders) ? data.recentOrders : []);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        if (!mounted) return;
        setError(err?.message || 'Failed to load dashboard data');
        setStats(null);
        setRecentUsers([]);
        setRecentProducts([]);
        setRecentOrders([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchDashboard();
    return () => {
      mounted = false;
    };
  }, []);
  
  if (loading) {
    return (
      <div className="w-100 d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <h3>Loading dashboard...</h3>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-100 d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <div>
          <h3 className="mb-2">Failed to load dashboard</h3>
          <div className="text-muted">{error}</div>
        </div>
      </div>
    );
  }
  
  return (
      <div className="admin-dashboard-page">
        <Container fluid className="py-4">
        <h2 className="mb-4">Admin Dashboard</h2>
        
        {/* Stats Cards */}
        <Row className="mb-4">
          <Col md={6} lg={3} className="mb-4">
            <Card className="shadow-sm h-100 border-left-primary">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-xs text-uppercase text-primary mb-1">
                      Total Users
                    </div>
                    <div className="h4 mb-0 font-weight-bold">{stats?.totalUsers ?? 0}</div>
                    <div className="text-muted small mt-2">
                      <span className="text-success">+{stats?.newUsersToday ?? 0}</span> new today
                    </div>
                  </div>
                  <div className="text-primary">
                    <FaUsers size={30} />
                  </div>
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0">
                <Link to="/admin/users" className="text-primary text-decoration-none small">
                  View Details &rarr;
                </Link>
              </Card.Footer>
            </Card>
          </Col>
          
          <Col md={6} lg={3} className="mb-4">
            <Card className="shadow-sm h-100 border-left-success">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-xs text-uppercase text-success mb-1">
                      Total Products
                    </div>
                    <div className="h4 mb-0 font-weight-bold">{stats?.totalProducts ?? 0}</div>
                    <div className="text-muted small mt-2">
                      <span className="text-success">+{stats?.newProductsToday ?? 0}</span> new today
                    </div>
                  </div>
                  <div className="text-success">
                    <FaStore size={30} />
                  </div>
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0">
                <Link to="/admin/products" className="text-success text-decoration-none small">
                  View Details &rarr;
                </Link>
              </Card.Footer>
            </Card>
          </Col>
          
          <Col md={6} lg={3} className="mb-4">
            <Card className="shadow-sm h-100 border-left-info">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-xs text-uppercase text-info mb-1">
                      Total Orders
                    </div>
                    <div className="h4 mb-0 font-weight-bold">{stats?.totalOrders ?? 0}</div>
                    <div className="text-muted small mt-2">
                      <span className="text-warning">{stats?.pendingOrders ?? 0}</span> pending
                    </div>
                  </div>
                  <div className="text-info">
                    <FaShoppingCart size={30} />
                  </div>
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0">
                <Link to="/admin/orders" className="text-info text-decoration-none small">
                  View Details &rarr;
                </Link>
              </Card.Footer>
            </Card>
          </Col>
          
          <Col md={6} lg={3} className="mb-4">
            <Card className="shadow-sm h-100 border-left-warning">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-xs text-uppercase text-warning mb-1">
                      Total Sales
                    </div>
                    <div className="h4 mb-0 font-weight-bold">GH₵ {(stats?.totalSales ?? 0).toFixed(2)}</div>
                    <div className="text-muted small mt-2">
                      <span className="text-danger">{stats?.reportedItems ?? 0}</span> reported items
                    </div>
                  </div>
                  <div className="text-warning">
                    <FaChartLine size={30} />
                  </div>
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0">
                <Link to="/admin/reports" className="text-warning text-decoration-none small">
                  View Reports &rarr;
                </Link>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
        
        <Row>
          {/* Recent Users */}
          <Col lg={6} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Header className="bg-white py-3">
                <h5 className="mb-0">Recent Users</h5>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Department</th>
                        <th>Role</th>
                        <th>Joined</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentUsers.map(user => (
                        <tr key={user.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="avatar-circle me-2">
                                {user.name && typeof user.name === 'string' ? user.name.charAt(0) : '?'}
                              </div>
                              <div>
                                <div className="fw-bold">{user.name || 'N/A'}</div>
                                <small className="text-muted">{user.email || 'N/A'}</small>
                              </div>
                            </div>
                          </td>
                          <td>{user.department?.name || user.department?.title || 'N/A'}</td>
                          <td>
                            <Badge 
                              bg={user.role === 'buyer' ? 'primary' : user.role === 'seller' ? 'success' : 'danger'}
                            >
                              {user.role || 'N/A'}
                            </Badge>
                          </td>
                          <td>{user.dateJoined ? new Date(user.dateJoined).toLocaleDateString() : 'N/A'}</td>
                          <td>
                            <div className="d-flex">
                              <Button 
                                as={Link}
                                to={`/admin/users/${user.id}`}
                                variant="outline-primary"
                                size="sm"
                                className="me-2"
                              >
                                <FaUserCheck />
                              </Button>
                              <Button 
                                variant="outline-danger"
                                size="sm"
                              >
                                <FaUserSlash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0">
                <Link to="/admin/users" className="text-primary text-decoration-none small">
                  View All Users &rarr;
                </Link>
              </Card.Footer>
            </Card>
          </Col>
          
          {/* Recent Products */}
          <Col lg={6} className="mb-4">
            <Card className="shadow-sm h-100">
              <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0"><FaStore className="me-2 text-success" /> Recent Products</h5>
                <Badge bg="success" pill>{recentProducts.length} new listings</Badge>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>Product</th>
                        <th>Seller</th>
                        <th>Price</th>
                        <th>Added</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentProducts.map(product => {
                        // Calculate days since added
                        const addedDate = new Date(product.dateAdded);
                        const today = new Date();
                        const daysSince = Math.floor((today - addedDate) / (1000 * 60 * 60 * 24));
                        
                        return (
                          <tr key={product.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="product-img me-2 rounded bg-light d-flex align-items-center justify-content-center" style={{width: '40px', height: '40px', overflow: 'hidden'}}>
                                  {product.category === 'Textbooks' && <FaBook className="text-primary" />}
                                  {product.category === 'Electronics' && <FaLaptop className="text-info" />}
                                  {product.category === 'Stationery' && <FaPencilAlt className="text-warning" />}
                                  {!['Textbooks', 'Electronics', 'Stationery'].includes(product.category) && <FaBox className="text-secondary" />}
                                </div>
                                <div>
                                  <div className="fw-bold">{product.name || 'N/A'}</div>
                                  <small className="text-muted">{product.category || 'N/A'}</small>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div className="seller-avatar bg-info text-white rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '28px', height: '28px' }}>
                                  {product.seller?.name && typeof product.seller.name === 'string' ? product.seller.name.charAt(0) : (product.seller && typeof product.seller === 'string' ? product.seller.charAt(0) : '?')}
                                </div>
                                <span>{product.seller?.name || product.seller || 'N/A'}</span>
                              </div>
                            </td>
                            <td>
                              <span className="fw-bold text-success">GH₵ {product.price.toFixed(2)}</span>
                            </td>
                            <td>
                              <span className={daysSince <= 2 ? 'text-success' : 'text-muted'}>
                                {daysSince === 0 ? 'Today' : 
                                 daysSince === 1 ? 'Yesterday' : 
                                 `${daysSince} days ago`}
                              </span>
                            </td>
                            <td>
                              <Badge 
                                bg={
                                  product.status === 'available' ? 'success' : 
                                  product.status === 'hidden' ? 'secondary' : 
                                  'danger'
                                }
                                pill
                              >
                                {product.status || 'N/A'}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex">
                                <Button 
                                  as={Link}
                                  to={`/admin/products/${product.id}`}
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  title="View product"
                                >
                                  <FaEye />
                                </Button>
                                <Button 
                                  variant="outline-danger"
                                  size="sm"
                                  title="Delete product"
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0">
                <Link to="/admin/products" className="text-success text-decoration-none small">
                  View All Products &rarr;
                </Link>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
        
        {/* Recent Orders */}
        <Row>
          <Col lg={12} className="mb-4">
            <Card className="shadow-sm">
              <Card.Header className="bg-white py-3">
                <h5 className="mb-0">Recent Orders</h5>
              </Card.Header>
              <Card.Body>
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead>
                      <tr>
                        <th>Order ID</th>
                        <th>Buyer</th>
                        <th>Items</th>
                        <th>Total</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentOrders.map(order => (
                        <tr key={order.id}>
                          <td>#{order.id}</td>
                          <td>{order.buyer?.name || order.buyer || 'N/A'}</td>
                          <td>{Array.isArray(order.items) ? order.items.length : (typeof order.items === 'string' ? order.items : 'N/A')}</td>
                          <td>GH₵ {order.total ? order.total.toFixed(2) : '0.00'}</td>
                          <td>{order.date ? new Date(order.date).toLocaleDateString() : 'N/A'}</td>
                          <td>
                            <Badge 
                              bg={
                                order.status === 'completed' ? 'success' : 
                                order.status === 'processing' ? 'primary' : 
                                order.status === 'pending' ? 'warning' : 
                                'danger'
                              }
                            >
                              {order.status || 'N/A'}
                            </Badge>
                          </td>
                          <td>
                            <Button 
                              as={Link}
                              to={`/admin/orders/${order.id}`}
                              variant="outline-primary"
                              size="sm"
                            >
                              <FaBoxOpen />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
              <Card.Footer className="bg-white border-0">
                <Link to="/admin/orders" className="text-info text-decoration-none small">
                  View All Orders &rarr;
                </Link>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
        </Container>
      </div>
  );
};

export default AdminDashboard;
